export type PapaDistrict = {
  id: string
  name: string
  short_name?: string
  parent_name?: string
}

export type PapaDeliveryRow = {
  deliveryId: number
  orderId: number
  date: string
  cost: number
  deliveryStatus: string
  deliveryProvider: string
  externalDeliveryId: string
  externalDeliveryCode: string
  externalDeliveryStatus: string
  externalDeliveryError: string
  papaPrice: number
  papaCreatedAt: string
  papaDistrict: string
  token: string
  town: string
  district: string
  unit: string
  street: string
  orderStatus: string
  ownName: string
  parentName: string
  companyName: string
  itemCount: number
}

export const isPapaDeliveryCreated = (delivery: any) => !!delivery?.externalDeliveryId

export const isPapaDeliveryConfirmed = (delivery: any) => {
  const status = String(delivery?.externalDeliveryStatus || '').toUpperCase()
  return status === 'CONFIRMED' || status === 'CONFIRM'
}

export const canSelectDeliveryForPapa = (delivery: any) => !isPapaDeliveryCreated(delivery)

export const canCreatePapaDeliveryForOrder = (order: any) => {
  if (!order || order.status !== 'CONFIRMED') return false
  // a self-pickup order has nothing to dispatch to a courier
  if (order.pickup) return false
  return (order.deliveries || []).some((delivery: any) => canSelectDeliveryForPapa(delivery))
}

export async function postDeliveryAction(
  apiUrl: string,
  token: string,
  path: string,
  body?: any,
): Promise<{ ok: boolean; data?: any; error?: string }> {
  const response = await fetch(`${apiUrl}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: token,
      'Content-Type': 'application/json',
    },
    body: body !== undefined ? JSON.stringify(body) : '',
  })

  if (response.ok) {
    try {
      const resJson = await response.json()
      return { ok: true, data: resJson.data }
    }
    catch {
      return { ok: true }
    }
  }

  try {
    const resJson = await response.json()
    return { ok: false, error: resJson.error || 'Papa хүргэлтийн үйлдэл амжилтгүй боллоо' }
  }
  catch {
    return { ok: false, error: 'Papa хүргэлтийн үйлдэл амжилтгүй боллоо' }
  }
}

export async function fetchPapaDistricts(
  apiUrl: string,
  token: string,
): Promise<{ ok: boolean; districts?: PapaDistrict[]; error?: string }> {
  const response = await fetch(`${apiUrl}mongol/delivery/papa/locations`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: token,
    },
  })

  if (!response.ok) {
    try {
      const resJson = await response.json()
      return { ok: false, error: resJson.error || 'Papa дүүргийн жагсаалт авахад алдаа гарлаа' }
    }
    catch {
      return { ok: false, error: 'Papa дүүргийн жагсаалт авахад алдаа гарлаа' }
    }
  }

  const resJson = await response.json()
  return { ok: true, districts: resJson.data || [] }
}

export type PapaDeliveriesReportFilter = {
  startDate: string
  endDate: string
  district?: string
  papaCreatedStartDate?: string
  papaCreatedEndDate?: string
  papaOrderId?: string
}

export async function fetchPapaDeliveriesReport(
  apiUrl: string,
  token: string,
  filter: PapaDeliveriesReportFilter,
): Promise<{ ok: boolean; deliveries?: PapaDeliveryRow[]; error?: string }> {
  const response = await fetch(`${apiUrl}mongol/report/papa-deliveries`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(filter),
  })

  if (!response.ok) {
    try {
      const resJson = await response.json()
      return { ok: false, error: resJson.error || 'Papa хүргэлтийн тайлан авахад алдаа гарлаа' }
    }
    catch {
      return { ok: false, error: 'Papa хүргэлтийн тайлан авахад алдаа гарлаа' }
    }
  }

  const resJson = await response.json()
  return { ok: true, deliveries: resJson.data || [] }
}

export async function createPapaDeliveries(
  apiUrl: string,
  token: string,
  deliveryIds: number[],
  toLocationId: string,
): Promise<{ ok: boolean; data?: any; error?: string; failedDeliveryId?: number }> {
  for (const deliveryId of deliveryIds) {
    const providerResult = await postDeliveryAction(
      apiUrl,
      token,
      `mongol/delivery/${deliveryId}/provider`,
      { provider: 'PAPA' },
    )
    if (!providerResult.ok) {
      return { ok: false, error: providerResult.error, failedDeliveryId: deliveryId }
    }
  }

  const createResult = await postDeliveryAction(
    apiUrl,
    token,
    'mongol/delivery/papa/create-batch',
    { deliveryIds, toLocationId },
  )
  if (!createResult.ok) {
    return { ok: false, error: createResult.error, failedDeliveryId: deliveryIds[0] }
  }

  return { ok: true, data: createResult.data }
}
